import { useProductList } from "@/api/services/admin/product";
import { useProductContractsByOrganization } from "@/api/services/admin/product-contract";
import { EContractStatus } from "@/types/enum";
import { useMemo } from "react";

/**
 * Resolves the contract ids that should be pre-selected by default on the create-user flow:
 * every product the org holds an ACTIVE (during-contract / trial) contract for.
 *
 * Lives at the page level so the page can gate rendering behind `isLoading` and hand the result to
 * the form as its initial value — the selection is applied once, deterministically, instead of via a
 * mount-timing-sensitive effect inside the child. Shares the same query keys as `UserProductSelect`,
 * so no extra network round-trip.
 */
export const useDefaultProductContractIds = (organizationId?: string, includeContractStatuses?: EContractStatus[]) => {
  const { data: productData, isLoading: isProductLoading } = useProductList();
  const { data: contractData, isLoading: isContractLoading } = useProductContractsByOrganization(organizationId, { includeContractStatuses });

  const defaultContractIds = useMemo(() => {
    const contractMap = new Map((contractData?.data ?? []).map((contract) => [contract.product?.code, contract]));
    return (productData?.data ?? [])
      .map((item) => contractMap.get(item.code))
      .filter(
        (contract): contract is NonNullable<typeof contract> =>
          contract != null &&
          (contract.status == null || contract.status === EContractStatus.DURING_CONTRACT || contract.status === EContractStatus.TRIAL),
      )
      .map((contract) => contract.id);
  }, [productData, contractData]);

  return { defaultContractIds, isLoading: isProductLoading || isContractLoading };
};
